import React, { createContext, useEffect, useMemo, useState } from "react";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import { designTokens } from "../theme/designTokens";

export const ThemeContext = createContext();

function savedPreference() {
  const saved = localStorage.getItem("themePreference");
  if (["system", "light", "dark"].includes(saved)) return saved;
  return localStorage.getItem("darkMode") === "true" ? "dark" : "light";
}

export const ThemeProvider = ({ children }) => {
  const [themePreference, setPreference] = useState(savedPreference);
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia("(prefers-color-scheme: dark)").matches,
  );
  const darkMode =
    themePreference === "system" ? systemDark : themePreference === "dark";
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event) => setSystemDark(event.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);
  const setThemePreference = (preference) => {
    if (!["system", "light", "dark"].includes(preference)) return;
    setPreference(preference);
    localStorage.setItem("themePreference", preference);
    localStorage.setItem("darkMode", String(preference === "dark"));
  };
  const toggleDarkMode = () => setThemePreference(darkMode ? "light" : "dark");
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            ...designTokens.colors.primary,
            main: darkMode ? "#8ab4ff" : "#2563eb",
            contrastText: darkMode ? "#132238" : "#ffffff",
          },
          secondary: designTokens.colors.secondary,
          success: {
            ...designTokens.colors.success,
            ...(darkMode ? { main: "#81d4a0", contrastText: "#132238" } : {}),
          },
          warning: {
            ...designTokens.colors.warning,
            ...(darkMode ? { main: "#f4c36a", contrastText: "#132238" } : {}),
          },
          error: {
            ...designTokens.colors.error,
            ...(darkMode ? { main: "#ffb4ad", contrastText: "#132238" } : {}),
          },
          info: {
            ...designTokens.colors.info,
            ...(darkMode ? { main: "#89caff", contrastText: "#132238" } : {}),
          },
          background: {
            default: darkMode ? "#0e192b" : "#f6f8fc",
            paper: darkMode ? "#17253b" : "#ffffff",
          },
          text: {
            primary: darkMode ? "#e8eef8" : "#17253b",
            secondary: darkMode ? "#afbed3" : "#526176",
          },
          divider: darkMode ? "#354861" : "#dce3ed",
        },
        typography: {
          ...designTokens.typography,
          fontFamily: designTokens.typography.fontFamily,
          h4: { fontSize: 30, fontWeight: 700, letterSpacing: "-0.025em" },
          h5: { fontSize: 22, fontWeight: 650 },
          h6: { fontSize: 18, fontWeight: 650 },
          button: { textTransform: "none", fontWeight: 600 },
        },
        shape: { borderRadius: 10 },
        breakpoints: { values: designTokens.breakpoints },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: { margin: 0 },
              a: { textUnderlineOffset: "3px" },
              "*:focus-visible": {
                outline: "3px solid #d18b00",
                outlineOffset: "3px",
              },
              "@media (prefers-reduced-motion: reduce)": {
                "*, *::before, *::after": {
                  animationDuration: "0.01ms !important",
                  transitionDuration: "0.01ms !important",
                  scrollBehavior: "auto !important",
                },
              },
            },
          },
          MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
              root: {
                borderRadius: 8,
                minHeight: 40,
                paddingInline: 16,
                "@media (max-width:959px)": { minHeight: 44 },
              },
              sizeSmall: {
                minHeight: 36,
                "@media (max-width:959px)": { minHeight: 44 },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                border: "1px solid " + (darkMode ? "#354861" : "#dce3ed"),
                boxShadow: "none",
                backgroundImage: "none",
              },
            },
          },
          MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
          MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 8 } } },
          MuiTableCell: {
            styleOverrides: {
              root: {
                borderColor: darkMode ? "#354861" : "#dce3ed",
                fontSize: 14,
              },
              head: {
                backgroundColor: darkMode ? "#1d2f49" : "#fafbfd",
                color: darkMode ? "#afbed3" : "#526176",
                fontWeight: 600,
                fontSize: 12,
              },
            },
          },
          MuiDialogTitle: {
            styleOverrides: { root: { fontSize: 20, fontWeight: 650 } },
          },
          MuiAlert: { styleOverrides: { root: { borderRadius: 8 } } },
        },
      }),
    [darkMode],
  );
  return (
    <ThemeContext.Provider
      value={{
        darkMode,
        toggleDarkMode,
        themePreference,
        setThemePreference,
        designTokens,
      }}
    >
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
